import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, Loader, Check } from "lucide-react";
import {
  listCMDBComponents,
  CMDBComponent,
} from "../../../../api/frameworksApi";

interface ComponentPickerProps {
  value: string; // componentId
  title: string; // componentTitle
  onChange: (id: string, title: string) => void;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const ComponentPicker: React.FC<ComponentPickerProps> = ({
  value,
  title,
  onChange,
  required = false,
  placeholder = "Search components",
  disabled = false,
}) => {
  const [query, setQuery] = useState(title || "");
  const [results, setResults] = useState<CMDBComponent[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const search = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const res = await listCMDBComponents({ q });
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(query), 300);
    return () => clearTimeout(t);
  }, [query, open, search]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onFocus={() => {
            if (!disabled) {
              setOpen(true);
              if (!query) search("");
            }
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            if (value) onChange("", ""); // Reset selection if typing
            setOpen(true);
          }}
          disabled={disabled}
          className="w-full pl-3 pr-10 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm disabled:opacity-50"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && <Loader className="size-3.5 animate-spin d" />}
          {!loading && value && <Check className="size-4 text-emerald-500" />}
          {!loading && !value && <Search className="size-4 d" />}
        </div>
      </div>

      {value && !open && (
        <p className="mt-1 text-[11px] text-indigo-600 font-medium px-1 truncate">
          Selected: {title}
        </p>
      )}

      {open && (
        <div className="absolute z-50 w-full mt-1 border border-border rounded-lg bg-background shadow-xl max-h-72 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
          {results.length === 0 ? (
            <div className="p-4 text-center text-sm d">
              {loading ? "Searching" : "No components found"}
            </div>
          ) : (
            <div className="p-1">
              {results.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange(c.id, c.title);
                    setQuery(c.title);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors flex flex-col gap-0.5 ${
                    value === c.id
                      ? "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400"
                      : "hover:bg-muted"
                  }`}
                >
                  <p className="text-sm font-medium truncate">{c.title}</p>
                  <p className="text-[10px] d tracking-wider">
                    {c.componentType || "SOFTWARE"}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
